import { appLocale } from "../../locales/display.ts";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { saveAs } from "file-saver";
import { Check, CheckCircle2, FileDown, LoaderCircle, RotateCcw, X } from "lucide-react";
import styled, { keyframes } from "styled-components";
import { runCursorExport, type CursorExportJob, type ExportProgress } from "./cursorExport";

const spin = keyframes`to { transform: rotate(360deg); }`;
const sweep = keyframes`from { transform: translateX(-120%); } to { transform: translateX(340%); }`;
const Overlay = styled.div`
	position: fixed; inset: 0; z-index: 11000; display: grid; place-items: center;
	padding: 20px; background: rgba(15, 23, 42, .42); backdrop-filter: blur(4px);
`;
const Dialog = styled.div`
	width: min(100%, 460px); box-sizing: border-box; padding: 28px; border-radius: 22px;
	background: #fff; color: #1a1d26; box-shadow: 0 24px 80px rgba(15,23,42,.24);
	max-height: 90dvh; overflow: auto; outline: none;
	h2 { margin: 18px 0 6px; font-size: 21px; font-weight: 600; }
	p { margin: 0; color: #667085; font-size: 13px; line-height: 1.6; }
	.spinning { animation: ${spin} 1s linear infinite; }
	@media (prefers-reduced-motion: reduce) { .spinning { animation: none; } }
`;
const Action = styled.button.attrs({ type: "button" })<{ $primary?: boolean }>`
	display: inline-flex; align-items: center; justify-content: center; gap: 8px;
	padding: 10px 16px; border: 1px solid ${p => p.$primary ? "#6d5ce8" : "#d0d5dd"}; border-radius: 9px;
	background: ${p => p.$primary ? "#6d5ce8" : "#fff"}; color: ${p => p.$primary ? "#fff" : "#475467"};
	font: inherit; font-size: 13px; cursor: pointer;
	&:hover { filter: brightness(.96); }
	&:focus-visible { outline: 2px solid #6d5ce8; outline-offset: 3px; }
`;
const Track = styled.div<{ $indeterminate: boolean; $value: number }>`
	height: 7px; background: #eeecfb; border-radius: 8px; overflow: hidden; margin: 12px 0 22px;
	> div { height: 100%; border-radius: inherit; background: #7b68ee; width: ${p => p.$indeterminate ? "30%" : `${p.$value}%`}; transition: width .25s; }
	&[data-indeterminate="true"] > div { animation: ${sweep} 1.5s ease-in-out infinite; }
	@media (prefers-reduced-motion: reduce) { > div { animation: none !important; transition: none; } }
`;
type Status = "running" | "success" | "error" | "cancelled";
const format = (value: number) => value.toLocaleString(appLocale("en-GB"));

/** Keep job stable for the lifetime of this popup (useMemo in the caller). */
export function CursorExportPopup<Row>({ job, title = msg("exportCsv"), description, onClose }: {
	job: CursorExportJob<Row>; title?: string; description?: string; onClose: () => void;
}) {
	useLocale();
	const titleId = useId();
	const dialog = useRef<HTMLDivElement>(null);
	const controller = useRef<AbortController>();
	const [attempt, setAttempt] = useState(0);
	const [status, setStatus] = useState<Status>("running");
	const [error, setError] = useState("");
	const [progress, setProgress] = useState<ExportProgress>({ phase: "loading", loaded: 0, pages: 0 });
	const closeRef = useRef(onClose);
	closeRef.current = onClose;

	useEffect(() => {
		const previousFocus = document.activeElement;
		const previousDialog = previousFocus instanceof HTMLElement ? previousFocus.closest('[role="dialog"]') : null;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		dialog.current?.focus();
		// Capture Escape so an underlying Panel cannot close together with this dialog.
		const escape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault(); event.stopImmediatePropagation();
			controller.current?.abort(); closeRef.current();
		};
		document.addEventListener("keydown", escape, true);
		return () => {
			controller.current?.abort();
			document.removeEventListener("keydown", escape, true);
			document.body.style.overflow = previousDialog && !previousDialog.isConnected ? "" : previousOverflow;
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
		};
	}, []);

	useEffect(() => {
		const abort = new AbortController();
		controller.current = abort;
		setStatus("running"); setError(""); setProgress({ phase: "loading", loaded: 0, pages: 0 });
		void runCursorExport(job, abort.signal, value => { if (!abort.signal.aborted) setProgress(value); }).then(file => {
			if (abort.signal.aborted) return;
			saveAs(file, job.filename);
			setStatus("success");
		}).catch(failure => {
			if (abort.signal.aborted) return;
			setError(failure instanceof Error ? failure.message : msg("couldNotCompleteTheExportPleaseTryAgain"));
			setStatus("error");
		});
		return () => abort.abort();
	}, [job, attempt]);

	const running = status === "running";
	const preparing = progress.phase === "preparing";
	const percent = status === "success" ? 100 : progress.total ? Math.min(99, Math.floor(progress.loaded / progress.total * 100)) : undefined;
	if (typeof document === "undefined") return null;
	return createPortal(<Overlay onKeyDown={event => event.stopPropagation()}>
		<Dialog ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={event => {
			if (event.key !== "Tab") return;
			const controls = dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
			const first = controls?.[0]; const last = controls?.[controls.length - 1];
			if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
		}}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<div style={{ padding: 13, display: "flex", borderRadius: 15, color: status === "success" ? "#079455" : "#6d5ce8", background: status === "success" ? "#ecfdf3" : "#f1effd" }}>{status === "success" ? <CheckCircle2 size={25} /> : <FileDown size={25} />}</div>
				<Action aria-label={running ? msg("cancelExportAndClose") : msg("close")} style={{ padding: 7, border: 0 }} onClick={() => { controller.current?.abort(); onClose(); }}><X size={18} /></Action>
			</div>
			<h2 id={titleId}>{status === "success" ? msg("yourCsvIsReady") : status === "cancelled" ? msg("exportCancelled") : status === "error" ? msg("exportInterrupted") : title}</h2>
			<p>{description}</p>
			<div style={{ marginTop: 24, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
				<div><strong style={{ fontSize: 27, fontVariantNumeric: "tabular-nums" }}>{format(progress.loaded)}</strong><span style={{ color: "#667085", fontSize: 13 }}> {progress.total !== undefined ? msg("templateOfRows_57a0dbaa", { v0: format(progress.total) }) : msg("rowsCollected")}</span></div>
				<span style={{ color: "#6d5ce8", fontSize: 13 }}>{percent !== undefined ? `${percent}%` : msg("templateBatches_0553888b", { v0: format(progress.pages) })}</span>
			</div>
			<Track role="progressbar" aria-label={msg("csvExportProgress")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
				aria-valuetext={msg("templateRowsCollectedBatches_1c83ade4", { v0: format(progress.loaded), v1: format(progress.pages) })} $value={percent ?? 0} $indeterminate={percent === undefined && running} data-indeterminate={percent === undefined && running}>
				<div />
			</Track>
			<div style={{ display: "grid", gap: 12, fontSize: 13, color: "#475467" }}>
				<div style={{ display: "flex", gap: 10, alignItems: "center" }}>{preparing || status === "success" ? <Check size={16} color="#079455" /> : <LoaderCircle size={16} className={running ? "spinning" : undefined} />}{msg("collectRecords")}<span style={{ marginLeft: "auto", color: "#98a2b3" }}>{format(progress.pages)}{msg("batches")}</span></div>
				<div style={{ display: "flex", gap: 10, alignItems: "center", opacity: preparing ? 1 : .5 }}>{status === "success" ? <Check size={16} color="#079455" /> : <FileDown size={16} />}{msg("createCsv")}</div>
			</div>
			<div role="status" aria-live="polite" style={{ marginTop: 20 }}>
				{status === "error" ? <p style={{ color: "#b42332" }}>{error}</p> : <p>{status === "success" ? msg("exportCompletedSuccessfully") : status === "cancelled" ? msg("noFileWasSavedYouCanStartAgain") : preparing ? msg("allRecordsCollectedPreparingYourFile") : msg("collectingRecordsInOrderTheDownloadWillStartWhenEveryBatchIsReady")}</p>}
			</div>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
				{running ? <Action onClick={() => { controller.current?.abort(); setStatus("cancelled"); }}>{msg("cancelExport")}</Action> : <>
					<Action onClick={onClose}>{msg("close")}</Action>
					{status !== "success" && <Action $primary onClick={() => setAttempt(value => value + 1)}><RotateCcw size={14} />{msg("startAgain")}</Action>}
				</>}
			</div>
		</Dialog>
	</Overlay>, document.body);
}
